import { useRef, useState, type ReactNode } from "react"
import { Check, Pencil, Trash2, X } from "lucide-react"
import type { Strategy, StrategyId } from "../types/strategy"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Dialog, DialogContent } from "../Dialog/Dialog"
import { Orb, type OrbKind } from "../Orb/Orb"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../Tooltip/Tooltip"

export interface StrategyListProps {
  strategies: Strategy[]
  activeId?: StrategyId | null
  onSelect?: (id: StrategyId) => void
  onRename?: (id: StrategyId, newName: string) => void
  onDelete?: (id: StrategyId) => void
  header?: ReactNode
  emptyState?: ReactNode
  className?: string
}

export function StrategyList({
  strategies,
  activeId,
  onSelect,
  onRename,
  onDelete,
  header,
  emptyState,
  className,
}: StrategyListProps) {
  const [editingId, setEditingId] = useState<StrategyId | null>(null)
  const [draft, setDraft] = useState("")
  const [confirmDeleteId, setConfirmDeleteId] = useState<StrategyId | null>(null)

  const beginRename = (s: Strategy) => {
    setEditingId(s.id)
    setDraft(s.name)
  }

  const commitRename = (s: Strategy) => {
    const value = draft.trim()
    if (value && value !== s.name) {
      onRename?.(s.id, value)
    }
    setEditingId(null)
    setDraft("")
  }

  return (
    <div className={cn("flex flex-col flex-1 min-h-0", className)}>
      {header}
      {strategies.length === 0 && emptyState}
      <div className="flex-1 overflow-y-auto py-1">
        {strategies.map((s) => {
          const isActive = s.id === activeId
          const isEditing = s.id === editingId
          return (
            <div
              key={s.id}
              className={cn(
                "group flex items-center h-8 px-3 gap-1 cursor-pointer transition-colors",
                isActive
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                s.dimmed && "opacity-60",
              )}
              onClick={() => { if (!isEditing) onSelect?.(s.id) }}
            >
              {isEditing ? (
                <div className="flex-1 flex items-center gap-0.5 min-w-0">
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(s)
                      if (e.key === "Escape") { setEditingId(null); setDraft("") }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 bg-transparent border-b border-primary outline-none text-body text-foreground min-w-0"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 [&_svg]:size-2.5"
                    onClick={(e) => { e.stopPropagation(); commitRename(s) }}
                    aria-label="Confirm rename"
                  >
                    <Check size={10} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 [&_svg]:size-2.5"
                    onClick={(e) => { e.stopPropagation(); setEditingId(null); setDraft("") }}
                    aria-label="Cancel rename"
                  >
                    <X size={10} />
                  </Button>
                </div>
              ) : (
                <>
                  <StrategyName name={s.name} className={isActive ? "text-body-strong" : "text-nav"} />
                  {s.running && <RunningIndicator orb={s.running} />}
                  {(onRename || onDelete) && (
                    <div
                      className={cn(
                        "items-center gap-0.5 shrink-0",
                        confirmDeleteId === s.id ? "flex" : "hidden group-hover:flex",
                      )}
                    >
                      {onRename && !s.noRename && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 [&_svg]:size-2.5 text-muted-foreground hover:text-foreground"
                          aria-label={`Rename ${s.name}`}
                          onClick={(e) => { e.stopPropagation(); beginRename(s) }}
                        >
                          <Pencil size={10} />
                        </Button>
                      )}
                      {onDelete && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 [&_svg]:size-2.5 text-muted-foreground hover:text-foreground"
                            aria-label={`Delete ${s.name}`}
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(s.id) }}
                          >
                            <Trash2 size={10} />
                          </Button>
                          <Dialog
                            open={confirmDeleteId === s.id}
                            onOpenChange={(open) => { if (!open) setConfirmDeleteId(null) }}
                          >
                            <DialogContent className="v1 sm:max-w-sm p-5 gap-0 overflow-hidden">
                              <h3 className="text-section text-foreground mb-1">
                                Delete &ldquo;{s.name}&rdquo;?
                              </h3>
                              <p className="text-caption text-muted-foreground leading-relaxed mb-4">
                                This cannot be undone.
                              </p>
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-caption"
                                  onClick={() => setConfirmDeleteId(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="text-caption"
                                  onClick={() => {
                                    onDelete(s.id)
                                    setConfirmDeleteId(null)
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * A live job on this strategy (D-p3-687), shown wherever the trader has navigated to, in the
 * same orb as its chat card. The
 * orb's smallest tuned size is 20px, so the row keeps its height by clipping to a centred
 * 16px box rather than scaling the orb off its tuning.
 */
function RunningIndicator({ orb }: { orb: OrbKind }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span role="img" aria-label="Running" className="shrink-0 flex h-4 w-4 items-center justify-center overflow-hidden">
            <Orb state={orb} size={20} aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">Running - you can work elsewhere and come back</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * The strategy's name, cut off with an ellipsis when it does not fit. Hovering a cut-off
 * name shows it in full; a name that fits shows nothing (PROD-3132). Measured when the
 * tooltip is about to open, because hovering the row also reveals the rename and delete
 * buttons, which take width from the name.
 */
function StrategyName({ name, className }: { name: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const truncated = () => {
    const el = ref.current
    return !!el && el.scrollWidth > el.clientWidth
  }
  return (
    // As the prototype has it (v6 TruncatedName): to the right of the name, after 300ms, and on
    // keyboard focus too. Not hoverable, and click-through: it sits over the row's rename and
    // delete buttons, which must stay reachable.
    <TooltipProvider delayDuration={300} disableHoverableContent>
      <Tooltip open={open} onOpenChange={(o) => setOpen(o && truncated())}>
        <TooltipTrigger asChild>
          <span ref={ref} tabIndex={0} className={cn("flex-1 min-w-0 truncate outline-none", className)}>
            {name}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="pointer-events-none whitespace-nowrap">
          {name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
